import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/utils/production-logger';

export interface AffiliateInfo {
  id: string;
  referral_code: string;
  name: string;
  email: string;
  status: 'pending' | 'active' | 'suspended';
  commission_rate: number;
  balance: number;
  total_earned: number;
  created_at: string;
  updated_at: string;
  ai_prop_ref_link?: string;
}

export interface AffiliateStats {
  totalReferrals: number;
  totalCommissions: number;
  totalSignups: number;
  totalClicks: number;
  conversionRate: number;
  // Legacy properties for backward compatibility
  signups: number;
  conversions: number;
  totalCommission: number;
}

export interface AdminAffiliateStats {
  totalAffiliates: number;
  activeAffiliates: number;
  pendingAffiliates: number;
  suspendedAffiliates: number;
  totalCommissions: number;
  totalReferrals: number;
  affiliateGrowth?: number;
  referralGrowth?: number;
  flaggedActivity?: number;
  pendingApplications?: Array<{
    name: string;
    email: string;
  }>;
  recentReferrals?: Array<{
    affiliateName: string;
    user: string;
    status: string;
    date: string;
  }>;
}

export interface Referral {
  id: string;
  referrer_id: string;
  referred_user_id: string;
  status: 'registered' | 'converted';
  created_at: string;
  updated_at: string;
  referral_code?: string;
  product?: string;
  converted_at?: string;
  commission_amount?: number;
  is_paid?: boolean;
}

export interface AdminReferral {
  id: string;
  affiliateName: string;
  userName: string;
  referralCode: string;
  status: 'registered' | 'converted' | 'expired';
  createdAt: string;
  ipAddress: string;
}

export interface ReferralCode {
  id: string;
  code: string;
  affiliate_id: string;
  description: string;
  expires_at?: string;
  created_at: string;
  is_active: boolean;
  clicks?: number;
  conversions?: number;
}

export interface FlaggedActivity {
  id: string;
  affiliateId: string;
  affiliateName: string;
  type: string;
  reason: string;
  status: 'new' | 'investigating' | 'resolved';
  createdAt: string;
}

export interface ReferredUser {
  id: string;
  masked_name: string;
  masked_email: string;
  status: 'registered' | 'converted';
  created_at: string;
  referral_code: string;
  product?: string;
}

class AffiliateService {
  // Get current user's affiliate info
  async getAffiliateInfo(): Promise<AffiliateInfo | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('affiliates')
      .select('*')
      .eq('id', user.id) // Fixed: Use 'id' instead of 'user_id'
      .maybeSingle();

    if (error) {
      console.error('Error fetching affiliate info:', error);
      return null;
    }

    return data;
  }

  // Get affiliate stats for current user
  async getAffiliateStats(timeframe: "7d" | "30d" | "ytd" | "all" = "30d"): Promise<AffiliateStats> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get affiliate record with total_earned
    const { data: affiliate } = await supabase
      .from('affiliates')
      .select('id, total_earned')
      .eq('id', user.id) // Fixed: Use 'id' instead of 'user_id'
      .single();

    if (!affiliate) {
      return {
        totalReferrals: 0,
        totalCommissions: 0,
        totalSignups: 0,
        totalClicks: 0,
        conversionRate: 0,
        signups: 0,
        conversions: 0,
        totalCommission: 0
      };
    }

    // Calculate date filter
    let dateFilter = '';
    const now = new Date();
    switch (timeframe) {
      case '7d':
        dateFilter = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case '30d':
        dateFilter = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case 'ytd':
        dateFilter = new Date(now.getFullYear(), 0, 1).toISOString();
        break;
    }

    // Get referral stats
    let referralQuery = supabase
      .from('referrals')
      .select('status')
      .eq('referrer_id', affiliate.id);

    if (dateFilter) {
      referralQuery = referralQuery.gte('created_at', dateFilter);
    }

    const { data: referrals } = await referralQuery;
    
    const totalReferrals = referrals?.length || 0;
    const totalSignups = referrals?.filter(r => r.status === 'registered').length || 0;
    const conversions = referrals?.filter(r => r.status === 'converted').length || 0;
    const conversionRate = totalSignups > 0 ? (conversions / totalSignups) * 100 : 0;

    // Use total_earned from affiliates table instead of querying affiliate_commissions
    const totalCommissions = affiliate.total_earned || 0;

    return {
      totalReferrals,
      totalCommissions,
      totalSignups,
      totalClicks: 0, // TODO: Implement click tracking
      conversionRate,
      // Legacy properties
      signups: totalSignups,
      conversions,
      totalCommission: totalCommissions
    };
  }

  // Get current user's referrals
  async getReferrals(): Promise<Referral[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data: affiliate } = await supabase
      .from('affiliates')
      .select('id')
      .eq('id', user.id) // Fixed: Use 'id' instead of 'user_id'
      .single();

    if (!affiliate) return [];

    const { data, error } = await supabase
      .from('referrals')
      .select('*')
      .eq('referrer_id', affiliate.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching referrals:', error);
      return [];
    }

    return data || [];
  }

  // FIXED: Get referred users with proper error handling
  async getReferredUsers(status: 'all' | 'registered' | 'converted' = 'all'): Promise<ReferredUser[]> {
    console.log('🔄 getReferredUsers called with status:', status);
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('❌ No authenticated user');
      return [];
    }

    try {
      // Get affiliate info first
      const { data: affiliate, error: affiliateError } = await supabase
        .from('affiliates')
        .select('id, referral_code')
        .eq('id', user.id) // Fixed: Use 'id' instead of 'user_id'
        .single();

      if (affiliateError || !affiliate) {
        console.log('❌ No affiliate found:', affiliateError);
        return [];
      }

      console.log('✅ Found affiliate:', affiliate.id);

      // Build query for referrals
      let referralQuery = supabase
        .from('referrals')
        .select(`
          id,
          referred_user_id,
          status,
          created_at,
          referral_code
        `)
        .eq('referrer_id', affiliate.id);

      // Apply status filter if not 'all'
      if (status !== 'all') {
        referralQuery = referralQuery.eq('status', status);
      }

      const { data: referrals, error: referralError } = await referralQuery
        .order('created_at', { ascending: false });

      if (referralError) {
        console.error('❌ Error fetching referrals:', referralError);
        return [];
      }

      if (!referrals || referrals.length === 0) {
        console.log('ℹ️ No referrals found');
        return [];
      }

      console.log('✅ Found referrals count:', referrals.length);

      // FIXED: Batch fetch user profiles using IN query (avoids N+1 issue)
      const userIds = referrals.map(r => r.referred_user_id);
      console.log('🔄 Fetching user profiles for IDs:', userIds.length);

      const { data: userProfiles, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_id, display_name, display_email')
        .in('user_id', userIds);

      if (profileError) {
        console.error('❌ Error fetching user profiles:', profileError);
        // Continue with empty profiles rather than failing
      }

      console.log('✅ Fetched user profiles count:', userProfiles?.length || 0);

      // Create a map for quick lookup
      const profileMap = new Map();
      userProfiles?.forEach(profile => {
        profileMap.set(profile.user_id, profile);
      });

      // Map referrals to ReferredUser format
      const referredUsers: ReferredUser[] = referrals.map(referral => {
        const profile = profileMap.get(referral.referred_user_id);
        
        // Mask sensitive data for privacy
        const maskEmail = (email: string) => {
          if (!email || !email.includes('@')) return email;
          const [local, domain] = email.split('@');
          const maskedLocal = local.length > 2 ? local[0] + '*'.repeat(local.length - 2) + local.slice(-1) : local;
          return `${maskedLocal}@${domain}`;
        };

        const maskName = (name: string) => {
          if (!name || name.length <= 2) return name;
          return name[0] + '*'.repeat(name.length - 2) + name.slice(-1);
        };

        return {
          id: referral.id,
          masked_name: profile?.display_name ? maskName(profile.display_name) : 'Unknown User',
          masked_email: profile?.display_email ? maskEmail(profile.display_email) : 'Unknown Email',
          status: referral.status,
          created_at: referral.created_at,
          referral_code: referral.referral_code || affiliate.referral_code,
          product: 'Standard' // Default product
        };
      });

      console.log('✅ Processed referred users count:', referredUsers.length);
      return referredUsers;

    } catch (error) {
      console.error('❌ Unexpected error in getReferredUsers:', error);
      return [];
    }
  }

  // Get current user's referral codes
  async getReferralCodes(): Promise<ReferralCode[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data: affiliate } = await supabase
      .from('affiliates')
      .select('id')
      .eq('id', user.id) // Fixed: Use 'id' instead of 'user_id'
      .single();

    if (!affiliate) return [];

    const { data, error } = await supabase
      .from('referral_codes')
      .select('*')
      .eq('affiliate_id', affiliate.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching referral codes:', error);
      return [];
    }

    return data || [];
  }

  // Create new referral code
  async createReferralCode(description: string, expiresAt?: Date): Promise<ReferralCode> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: affiliate } = await supabase
      .from('affiliates')
      .select('id')
      .eq('id', user.id) // Fixed: Use 'id' instead of 'user_id'
      .single();

    if (!affiliate) throw new Error('Not an affiliate');

    // Generate unique code
    const code = 'REF' + Math.random().toString(36).substr(2, 9).toUpperCase();

    const { data, error } = await supabase
      .from('referral_codes')
      .insert({
        code,
        affiliate_id: affiliate.id,
        description,
        expires_at: expiresAt?.toISOString(),
        is_active: true
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating referral code:', error);
      throw error;
    }

    return data;
  }

  // Apply for affiliate program
  async applyForAffiliate(applicationData: {
    name: string;
    email: string;
    reason: string;
    website?: string;
    social_media?: string;
  }): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Generate referral code
    const referralCode = 'REF' + Math.random().toString(36).substr(2, 9).toUpperCase();

    const { error } = await supabase
      .from('affiliates')
      .insert({
        id: user.id, // Primary key references auth.users(id)
        referral_code: referralCode,
        name: applicationData.name,
        email: applicationData.email,
        status: 'pending',
        commission_rate: 0.05, // Default 5%
        balance: 0,
        total_earned: 0,
        // Use separate columns that exist in database schema
        website: applicationData.website,
        social_media: applicationData.social_media,
        application_reason: applicationData.reason
      });

    if (error) {
      console.error('Error applying for affiliate:', error);
      throw error;
    }
  }

  // Record referral click
  async recordReferralClick(referralCode: string, ipAddress?: string): Promise<void> {
    const { error } = await supabase
      .from('referral_clicks')
      .insert({
        referral_code: referralCode,
        ip_address: ipAddress || 'unknown',
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });

    if (error) {
      console.error('Error recording referral click:', error);
    }
  }

  // Get affiliate by referral code
  async getAffiliateByReferralCode(referralCode: string): Promise<AffiliateInfo | null> {
    const { data, error } = await supabase
      .from('affiliates')
      .select('*')
      .eq('referral_code', referralCode)
      .eq('status', 'active')
      .single();

    if (error) {
      console.error('Error fetching affiliate by referral code:', error);
      return null;
    }

    return data;
  }

  // Update AI prop ref link
  async updateAiPropRefLink(affiliateId: string, refLink: string): Promise<boolean> {
    const { error } = await supabase
      .from('affiliates')
      .update({ ai_prop_ref_link: refLink })
      .eq('id', affiliateId);

    if (error) {
      console.error('Error updating AI prop ref link:', error);
      return false;
    }

    return true;
  }

  // ADMIN METHODS
  
  // Get admin affiliate stats
  async getAdminAffiliateStats(): Promise<AdminAffiliateStats> {
    const { data: affiliates } = await supabase
      .from('affiliates')
      .select('status, total_earned');

    const stats = {
      totalAffiliates: affiliates?.length || 0,
      activeAffiliates: affiliates?.filter(a => a.status === 'active').length || 0,
      pendingAffiliates: affiliates?.filter(a => a.status === 'pending').length || 0,
      suspendedAffiliates: affiliates?.filter(a => a.status === 'suspended').length || 0,
      totalCommissions: 0,
      totalReferrals: 0
    };

    // Calculate total commissions from total_earned field in affiliates table
    stats.totalCommissions = affiliates?.reduce((sum, a) => sum + (a.total_earned || 0), 0) || 0;

    // Get referral stats
    const { data: referrals } = await supabase
      .from('referrals')
      .select('id');

    stats.totalReferrals = referrals?.length || 0;

    return stats;
  }

  // Get all affiliates for admin
  async getAdminAffiliates(statusFilter: string = 'all'): Promise<AffiliateInfo[]> {
    let query = supabase
      .from('affiliates')
      .select('*');

    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    }

    const { data, error } = await query
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching admin affiliates:', error);
      return [];
    }

    return data || [];
  }

  // Update affiliate status
  async updateAffiliateStatus(affiliateId: string, status: 'active' | 'suspended' | 'pending'): Promise<boolean> {
    const { error } = await supabase
      .from('affiliates')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', affiliateId);

    if (error) {
      console.error('Error updating affiliate status:', error);
      return false;
    }

    return true;
  }

  // Update affiliate commission rate
  async updateAffiliateCommissionRate(affiliateId: string, rate: number): Promise<boolean> {
    const { error } = await supabase
      .from('affiliates')
      .update({ commission_rate: rate, updated_at: new Date().toISOString() })
      .eq('id', affiliateId);

    if (error) {
      console.error('Error updating commission rate:', error);
      return false;
    }

    return true;
  }

  // Get admin referrals using separate queries to avoid PostgREST relationship errors
  async getAdminReferrals(statusFilter: string = 'all', page: number = 1, pageSize: number = 50): Promise<{data: AdminReferral[], total: number}> {
    try {
      console.log('🔄 Getting admin referrals with separate queries...');

      // Step 1: Get referrals with basic filtering
      let referralQuery = supabase
        .from('referrals')
        .select('*', { count: 'exact' });

      if (statusFilter !== 'all') {
        referralQuery = referralQuery.eq('status', statusFilter);
      }

      const { data: referrals, error: referralError, count } = await referralQuery
        .order('created_at', { ascending: false })
        .range((page - 1) * pageSize, page * pageSize - 1);

      if (referralError) {
        console.error('❌ Error fetching referrals:', referralError);
        return { data: [], total: 0 };
      }

      if (!referrals || referrals.length === 0) {
        console.log('ℹ️ No referrals found');
        return { data: [], total: 0 };
      }

      console.log('✅ Found referrals:', referrals.length);

      // Step 2: Get affiliate names separately
      const affiliateIds = [...new Set(referrals.map(r => r.referrer_id).filter(Boolean))];
      const { data: affiliates, error: affiliateError } = await supabase
        .from('affiliates')
        .select('id, name, referral_code')
        .in('id', affiliateIds);

      if (affiliateError) {
        console.error('⚠️ Error fetching affiliates:', affiliateError);
      }

      // Step 3: Get user profiles separately
      const userIds = [...new Set(referrals.map(r => r.referred_user_id).filter(Boolean))];
      const { data: userProfiles, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_id, display_name, display_email')
        .in('user_id', userIds);

      if (profileError) {
        console.error('⚠️ Error fetching user profiles:', profileError);
      }

      // Step 4: Create lookup maps
      const affiliateMap = new Map(affiliates?.map(a => [a.id, a]) || []);
      const userMap = new Map(userProfiles?.map(u => [u.user_id, u]) || []);

      // Step 5: Combine data
      const adminReferrals: AdminReferral[] = referrals.map(referral => {
        const affiliate = affiliateMap.get(referral.referrer_id);
        const user = userMap.get(referral.referred_user_id);

        return {
          id: referral.id,
          affiliateName: affiliate?.name || 'Unknown Affiliate',
          userName: user?.display_name || user?.display_email || 'Unknown User',
          referralCode: affiliate?.referral_code || 'Unknown',
          status: referral.status,
          createdAt: referral.created_at,
          ipAddress: referral.ip_address || 'Unknown'
        };
      });

      console.log('✅ Successfully processed admin referrals:', adminReferrals.length);

      return {
        data: adminReferrals,
        total: count || 0
      };

    } catch (error) {
      console.error('❌ Unexpected error in getAdminReferrals:', error);
      return { data: [], total: 0 };
    }
  }

  // Get flagged activities
  async getFlaggedActivities(): Promise<FlaggedActivity[]> {
    const { data, error } = await supabase
      .from('flagged_activities')
      .select(`
        *,
        affiliates!flagged_activities_affiliate_id_fkey(name)
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching flagged activities:', error);
      return [];
    }

    return (data || []).map(activity => ({
      id: activity.id,
      affiliateId: activity.affiliate_id,
      affiliateName: activity.affiliates?.name || 'Unknown',
      type: activity.type,
      reason: activity.reason,
      status: activity.status,
      createdAt: activity.created_at
    }));
  }

  // Dismiss flagged activity
  async dismissFlaggedActivity(id: string): Promise<void> {
    const { error } = await supabase
      .from('flagged_activities')
      .update({ status: 'resolved' })
      .eq('id', id);

    if (error) {
      console.error('Error dismissing flagged activity:', error);
      throw error;
    }
  }

  // Mark flagged activity as investigating
  async markFlaggedActivityAsInvestigating(id: string): Promise<void> {
    const { error } = await supabase
      .from('flagged_activities')
      .update({ status: 'investigating' })
      .eq('id', id);

    if (error) {
      console.error('Error marking activity as investigating:', error);
      throw error;
    }
  }

  // STATIC METHODS

  // FIXED: Static method to get affiliate by user ID using maybeSingle()
  static async getAffiliateByUserId(userId: string): Promise<AffiliateInfo | null> {
    try {
      const { data, error } = await supabase
        .from('affiliates')
        .select('*')
        .eq('id', userId) // Fixed: Use 'id' instead of 'user_id' since id is the primary key referencing auth.users(id)
        .maybeSingle();

      if (error) {
        logger.error('Database error in getAffiliateByUserId', {
          component: 'AffiliateService',
          action: 'getAffiliateByUserId',
          userId
        }, error);
        return null;
      }

      return data;
    } catch (error) {
      logger.error('Unexpected error in getAffiliateByUserId', {
        component: 'AffiliateService',
        action: 'getAffiliateByUserId',
        userId
      }, error as Error);
      return null;
    }
  }
}

// Export singleton instance
export const affiliateService = new AffiliateService();

// Export class for static methods
export { AffiliateService };
